"use client";

import type React from "react";

import { motion, AnimatePresence } from "framer-motion";
import {
  Camera,
  Palette,
  Sparkles,
  Wand2,
  Type,
  Settings,
  ChevronDown,
  RefreshCw,
  Send,
  VideoIcon,
  Heart,
  Download,
  Zap,
  History,
  X,
  Share,
  UploadCloud,
} from "lucide-react";
import { useState, useRef, useEffect } from "react";
import eventBus from "../../utils/eventBus";
import { GoogleGenAI, Modality } from "@google/genai";
import uploadImageToFirebase from "../../utils/uploadImageToFirebase";
import PublishContent from "./PublishContent";
import { useDispatch, useSelector } from "react-redux";
import {
  addLibraryItem,
  selectLibraryContent,
  selectUserGlobalData,
  incrementImageEdits,
} from "../../store/dreamworldSlice";
import { useNavigate } from "react-router-dom";
import dreamWorldProvider from "../../DreamWorldProvider";
import { LibraryItem, InteractiveEditingData } from "../../types/library";
import { AppDispatch } from "../../store/store";
import UploadsPanel from "../uploads/UploadsPanel";

import { v4 as uuidv4 } from "uuid";
import { Timestamp } from "firebase/firestore";
import { getBlob, ref } from "firebase/storage";
import { storage } from "../../utils/firebase";
import LoadingIndicator from "../ui/LoadingIndicator";
import { hasEnoughFlux, decreaseFluxBalance } from "../../utils/fluxManager";
import InsufficientFluxModal from "../modals/InsufficientFluxModal";
import { Effect } from "../../types/effects";

interface ImageEditorProps {
  prompt?: string;
  effect?: Effect;
  isTesting?: boolean;
}

const ImageEditor: React.FC<ImageEditorProps> = ({
  prompt: initialPrompt,
  effect,
  isTesting,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const userGlobalData = useSelector(selectUserGlobalData);
  const libraryContent = useSelector(selectLibraryContent);

  const [prompt, setPrompt] = useState(
    effect?.prompt || initialPrompt || ""
  );
  const [isFocused, setIsFocused] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState("Photorealistic");
  const [stylesOpen, setStylesOpen] = useState(true);
  const [quickIdeasOpen, setQuickIdeasOpen] = useState(true);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [isGenerating, setIsGenerating] = useState(false);
  const [editedImage, setEditedImage] = useState<LibraryItem | null>(null);
  const [originalImage, setOriginalImage] = useState<File | null>(null);
  const [originalImagePreview, setOriginalImagePreview] = useState<
    string | null
  >(null);

  const [history, setHistory] = useState<LibraryItem[]>([]);
  const [currentHistoryId, setCurrentHistoryId] = useState<string | null>(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [selectedImageForPublish, setSelectedImageForPublish] =
    useState<LibraryItem | null>(null);
  const [isUploadsPanelOpen, setIsUploadsPanelOpen] = useState(false);
  const [overlayImage, setOverlayImage] = useState<string | null>(null);

  const resultsRef = useRef<HTMLDivElement>(null);

  const [hasSufficientFlux, setHasSufficientFlux] = useState(true);
  const [isFluxModalOpen, setIsFluxModalOpen] = useState(false);

  useEffect(() => {
    if (userGlobalData) {
      hasEnoughFlux(userGlobalData.user.uid, 15).then(setHasSufficientFlux);
    }
  }, [userGlobalData]);

  useEffect(() => {
    if (editedImage && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [editedImage]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const savedHistory = localStorage.getItem("imageEditorHistory");
    if (savedHistory) {
      try {
        const parsedHistory = JSON.parse(savedHistory);
        setHistory(parsedHistory);
      } catch (error) {
        console.error("Error loading history:", error);
      }
    }
  }, []);

  useEffect(() => {
    if (history.length > 0) {
      const limitedHistory = history.slice(0, 100);
      localStorage.setItem(
        "imageEditorHistory",
        JSON.stringify(limitedHistory)
      );
    }
  }, [history]);

  useEffect(() => {
    const handleHistoryUpdate = () => {
      try {
        const raw = localStorage.getItem("imageEditorHistory");
        if (raw) {
          setHistory(JSON.parse(raw));
        }
      } catch (err) {
        console.error("Failed to load history:", err);
      }
    };

    eventBus.on("imageEditorHistory:updated", handleHistoryUpdate);

    handleHistoryUpdate();

    return () => {
      eventBus.off("imageEditorHistory:updated", handleHistoryUpdate);
    };
  }, []);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(
        textareaRef.current.scrollHeight,
        120
      )}px`;
    }
  }, [prompt]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setOriginalImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setOriginalImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGenerate = async () => {
    if (!prompt || !originalImage || !userGlobalData) return;
    if (isTesting && !effect) return;

    if (!isTesting) {
      if (!hasSufficientFlux) {
        setIsFluxModalOpen(true);
        return;
      }
      const success = await decreaseFluxBalance(userGlobalData.user.uid, 15);
      if (!success) {
        setIsGenerating(false);
        setHasSufficientFlux(false);
        setIsFluxModalOpen(true);
        return;
      }
    }
    setIsGenerating(true);
    setEditedImage(null);

    try {
      const originalImageUrl = await uploadImageToFirebase(
        originalImage,
        "originalImages"
      );

      const ai = dreamWorldProvider.getImageAI();
      const fileAsBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(originalImage);
        reader.onload = () => resolve((reader.result as string).split(",")[1]);
        reader.onerror = (error) => reject(error);
      });

      const contents = [
        { text: `${selectedStyle} style: ${prompt}` },
        {
          inlineData: {
            mimeType: originalImage.type,
            data: fileAsBase64,
          },
        },
      ];

      const response = await ai.models.generateContent({
        model: "gemini-2.5-flash-image-preview",
      
        contents: contents,
        config: {
          responseModalities: [Modality.IMAGE],
        },
      });

      if (
        response.candidates &&
        response.candidates.length > 0 &&
        response.candidates[0].content &&
        response.candidates[0].content.parts
      ) {
        for (const part of response.candidates[0].content.parts) {
          if (part.inlineData && part.inlineData.data) {
            const imageData = part.inlineData.data;
            const byteCharacters = atob(imageData);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: "image/png" });
            const file = new File([blob], "edited_image.png", {
              type: "image/png",
            });
            const downloadUrl = await uploadImageToFirebase(
              file,
              "postsImages"
            );
            const docId = uuidv4();
            const newLibraryItem: LibraryItem = {
              id: docId,
              ownerId: userGlobalData.user.uid,
              type: "Image",
              fullUrl: downloadUrl,
              thumbnailUrl: downloadUrl,
              originalImageUrl,
              prompt: prompt,
              isFavorite: false,
              views: 0,
              likes: 0,
              tags: [selectedStyle.toLowerCase()],
              size: `${(blob.size / 1024 / 1024).toFixed(2)} MB`,
              createdAt: Timestamp.now(),
              isPublished: false,
              title: prompt,
              effectId: effect?.id,
            };

            await dreamWorldProvider.saveLibraryItem(newLibraryItem);
            dispatch(addLibraryItem(newLibraryItem));
            dispatch(incrementImageEdits());

            setEditedImage(newLibraryItem);
            setHistory((prev) => [newLibraryItem, ...prev]);
          }
        }
      }
    } catch (error) {
      console.error("Error editing image:", error);
    }

    setIsGenerating(false);
  };

  useEffect(() => {
    if (prompt.trim() && originalImage) {
      eventBus.emit("fab:config", {
        icon: "generate",
        text: "Generate",
        action: handleGenerate,
      });
    } else {
      eventBus.emit("fab:reset");
    }
  }, [prompt, originalImage, userGlobalData, hasSufficientFlux]);

  const handlePromptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setPrompt(e.target.value);
  };

  const loadHistoryItem = (historyItem: LibraryItem) => {
    setPrompt(historyItem.prompt as string);
    setSelectedStyle(historyItem.tags[0]);
    setEditedImage(historyItem);
    setCurrentHistoryId(historyItem.id);
    setIsHistoryOpen(false);
    setOriginalImage(null);
    setOriginalImagePreview(historyItem.originalImageUrl || null);
  };

  const handlePublish = (image: LibraryItem) => {
    setSelectedImageForPublish(image);
    setPublishModalOpen(true);
  };

  const navigate = useNavigate();

  const handleContinueEditing = async (image: LibraryItem) => {
    if (!userGlobalData) return;

    const docId = uuidv4();
    const newEditingData: InteractiveEditingData = {
      id: docId,
      ownerId: userGlobalData.user.uid,
      title: image.title || "Untitled Editing Session",
      originalImageUrls: [image.originalImageUrl || image.fullUrl],
      createdAt: Timestamp.now(),
      turnsCount: 1,
      editings: [
        {
          id: uuidv4(),
          imageUrl: [image.fullUrl],
          request: image.prompt || "Continue editing",
          generatedImageUrl: image.fullUrl,
          date: Timestamp.now(),
          isSavedToLib: true,
          isPublished: image.isPublished || false,
        },
      ],
    };

    await dreamWorldProvider.addInteractiveEditingData(newEditingData);
    navigate(`/interactive-editing/${docId}`);
  };

  const handleImageSelect = async (image: LibraryItem) => {
    try {
      setIsUploadsPanelOpen(false);
      setOriginalImagePreview(image.fullUrl);
      // Extract object path from fullUrl
      const match = decodeURIComponent(image.fullUrl).match(/o\/(.*?)\?/);
      if (!match) throw new Error("Invalid Firebase URL");
      const objectPath = match[1]; // e.g. "posts/3096be64-34ee-408f-a16e-3e32c9fbc40b"
      const imageRef = ref(storage, objectPath);
      const blob = await getBlob(imageRef);
      const file = new File([blob], image.title || "image.png", {
        type: blob.type,
      });
      setOriginalImage(file);
    } catch (error) {
      console.error("Error fetching image:", error);
    }
  };

  const styles = [
    {
      name: "Photo",
      value: "Photorealistic",
      icon: Camera,
      gradient: "from-emerald-500 to-teal-600",
    },
    {
      name: "Digital",
      value: "Digital art",
      icon: Palette,
      gradient: "from-purple-500 to-pink-600",
    },
    {
      name: "Cinematic",
      value: "Cinematic",
      icon: VideoIcon,
      gradient: "from-orange-500 to-red-600",
    },
    {
      name: "Anime",
      value: "Anime",
      icon: Sparkles,
      gradient: "from-blue-500 to-cyan-600",
    },
    {
      name: "Abstract",
      value: "Abstract",
      icon: Wand2,
      gradient: "from-violet-500 to-purple-600",
    },
    {
      name: "Minimal",
      value: "Minimalist",
      icon: Type,
      gradient: "from-gray-500 to-slate-600",
    },
  ];

  const examplePrompts = [
    "Add a hat to the person",
    "Change the background to a beach",
    "Make the image black and white",
    "Add a vintage film effect",
  ];

  return (
    <div className="flex flex-col lg:flex-row lg:h-[calc(100vh-73px)]">
      {/* Left Panel - Controls */}
      <div className="lg:w-96 flex-shrink-0 p-4 border-b lg:border-b-0 lg:border-r border-white/10 lg:overflow-y-auto">
        <div className="mb-4">
          <motion.button
            onClick={() => setIsHistoryOpen(true)}
            disabled={history.length === 0}
            className="w-full flex items-center justify-center gap-2 p-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg hover:bg-white/10 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            whileHover={{ scale: history.length > 0 ? 1.02 : 1 }}
            whileTap={{ scale: history.length > 0 ? 0.98 : 1 }}
          >
            <History size={16} className="text-purple-400" />
            <span className="text-sm font-medium text-white/90">
              History ({history.length})
            </span>
          </motion.button>
        </div>

        {/* Image Upload */}
        <div className="mb-6">
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
            ref={fileInputRef}
          />
          <motion.div
            onClick={() => setIsUploadsPanelOpen(true)}
            whileHover={{ scale: 1.02 }}
            className="cursor-pointer p-6 bg-white/5 backdrop-blur-sm border-2 border-dashed border-white/20 rounded-xl hover:bg-white/10 transition-all duration-300 text-center"
          >
            {originalImagePreview ? (
              <img
                src={originalImagePreview}
                alt="Original"
                className="w-full h-48 object-contain rounded-lg"
              />
            ) : (
              <div className="flex flex-col items-center justify-center text-white/60">
                <UploadCloud size={48} className="mb-4" />
                <p className="font-semibold">Click to select an image</p>
                <p className="text-sm">
                  Choose from your uploads or add a new one
                </p>
              </div>
            )}
          </motion.div>
        </div>

        {effect ? (
          <div className="mb-6">
            <div className="p-4 bg-white/5 rounded-xl border-2 border-white/10">
              <h3 className="text-sm font-medium text-white/90 mb-2">
                Effect Prompt
              </h3>
              <p className="text-sm text-white/70">{effect.prompt}</p>
            </div>
            <motion.button
              onClick={handleGenerate}
              disabled={
                !prompt || isGenerating || !originalImage || !hasSufficientFlux
              }
              className="w-full mt-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white p-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
              whileHover={{
                scale:
                  prompt && !isGenerating && originalImage && hasSufficientFlux
                    ? 1.02
                    : 1,
              }}
              whileTap={{
                scale:
                  prompt && !isGenerating && originalImage && hasSufficientFlux
                    ? 0.98
                    : 1,
              }}
            >
              {isGenerating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <Wand2 size={16} />
                  <span>Generate with Effect</span>
                </>
              )}
            </motion.button>
          </div>
        ) : (
          <div className="mb-6">
            <div
              className={`relative bg-white/5 backdrop-blur-xl border-2 ${
                isFocused ? "border-purple-500/50" : "border-white/10"
              } rounded-xl transition-all duration-300 overflow-hidden`}
            >
              <textarea
                ref={textareaRef}
                value={prompt}
                onChange={handlePromptChange}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                placeholder="Describe the edits you want to make..."
                className="w-full bg-transparent p-4 text-sm placeholder-white/40 text-white focus:outline-none resize-none overflow-hidden leading-relaxed"
                rows={3}
              />
              <motion.button
                onClick={handleGenerate}
                disabled={
                  !prompt ||
                  isGenerating ||
                  !originalImage ||
                  !hasSufficientFlux
                }
                className="absolute bottom-2 right-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white p-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
                whileHover={{
                  scale:
                    prompt &&
                    !isGenerating &&
                    originalImage &&
                    hasSufficientFlux
                      ? 1.05
                      : 1,
                }}
                whileTap={{
                  scale:
                    prompt &&
                    !isGenerating &&
                    originalImage &&
                    hasSufficientFlux
                      ? 0.95
                      : 1,
                }}
              >
                {isGenerating ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Send size={16} />
                    <div className="flex items-center gap-1 text-xs">
                      <Zap size={12} />
                      <span>15</span>
                    </div>
                  </>
                )}
              </motion.button>
            </div>
          </div>
        )}

        {/* Style Selection */}
        <div className="mb-6">
          <motion.button
            onClick={() => setStylesOpen(!stylesOpen)}
            className="flex items-center justify-between w-full"
          >
            <h3 className="text-sm font-medium text-white/90 flex items-center gap-2">
              <Palette size={16} />
              Style
            </h3>
            <motion.div
              animate={{ rotate: stylesOpen ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <ChevronDown size={16} className="text-white/60" />
            </motion.div>
          </motion.button>
          <AnimatePresence>
            {stylesOpen && (
              <motion.div
                initial={false}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div
                  className="mt-3 flex overflow-x-auto gap-2 pb-2"
                  style={{ scrollbarWidth: "none" }}
                >
                  <div className="grid grid-rows-2 grid-flow-col gap-2">
                    {styles.map((style) => {
                      const Icon = style.icon;
                      return (
                        <motion.button
                          key={style.name}
                          onClick={() => setSelectedStyle(style.value)}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className={`relative p-3 rounded-lg transition-all duration-300 ${
                            selectedStyle === style.value
                              ? "bg-white/10 border-2 border-purple-500/50"
                              : "bg-white/5 border-2 border-white/10 hover:bg-white/10"
                          }`}
                        >
                          <div
                            className={`w-6 h-6 rounded-md bg-gradient-to-r ${style.gradient} flex items-center justify-center mb-1 mx-auto`}
                          >
                            <Icon size={12} className="text-white" />
                          </div>
                          <p className="text-xs font-medium text-white/80">
                            {style.name}
                          </p>
                        </motion.button>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Quick Prompts */}
        <div className="mb-6">
          <motion.button
            onClick={() => setQuickIdeasOpen(!quickIdeasOpen)}
            className="flex items-center justify-between w-full"
          >
            <h3 className="text-sm font-medium text-white/90 flex items-center gap-2">
              <Zap size={16} />
              Quick Ideas
            </h3>
            <motion.div
              animate={{ rotate: quickIdeasOpen ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <ChevronDown size={16} className="text-white/60" />
            </motion.div>
          </motion.button>
          <AnimatePresence>
            {quickIdeasOpen && (
              <motion.div
                initial={false}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div
                  className="mt-3 flex overflow-x-auto gap-2 pb-2"
                  style={{ scrollbarWidth: "none" }}
                >
                  <div className="grid grid-rows-2 grid-flow-col gap-2">
                    {examplePrompts.map((examplePrompt, index) => (
                      <motion.button
                        key={index}
                        onClick={() => setPrompt(examplePrompt)}
                        whileHover={{ scale: 1.01 }}
                        className="w-40 text-left p-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg hover:bg-white/10 transition-all duration-300 group"
                      >
                        <p className="text-xs text-white/70 group-hover:text-white transition-colors">
                          {examplePrompt}
                        </p>
                      </motion.button>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Right Panel - Results */}
      <div className="flex-1 p-4 lg:overflow-y-auto" ref={resultsRef}>
        <AnimatePresence mode="wait">
          {isGenerating ? (
            <LoadingIndicator key="loading" size="w-16 h-16" />
          ) : editedImage ? (
            <ResultsGrid
              key="results"
              image={editedImage}
              originalImage={originalImagePreview}
              onPublish={handlePublish}
              onImageClick={setOverlayImage}
              onContinueEditing={handleContinueEditing}
            />
          ) : (
            <EmptyState key="empty" />
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {isHistoryOpen && (
          <motion.div
            initial={false}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex justify-end"
            onClick={() => setIsHistoryOpen(false)}
          >
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="w-full max-w-md bg-gray-900/95 backdrop-blur-xl border-l border-white/10 p-6 overflow-y-auto pt-16 md:pt-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <History size={20} className="text-purple-400" />
                  Editing History
                </h2>
                <button
                  onClick={() => setIsHistoryOpen(false)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X size={20} className="text-white/60" />
                </button>
              </div>

              <div className="space-y-3">
                {history.map((item) => (
                  <motion.div
                    key={item.id}
                    onClick={() => loadHistoryItem(item)}
                    className={`p-4 rounded-lg border cursor-pointer transition-all duration-300 hover:bg-white/10 ${
                      currentHistoryId === item.id
                        ? "bg-purple-500/20 border-purple-500/50"
                        : "bg-white/5 border-white/10"
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex items-start gap-3">
                      <div className="grid grid-cols-2 gap-1 w-16 h-16 flex-shrink-0">
                        <img
                          src={
                            item.thumbnailUrl ||
                            "https://plus.unsplash.com/premium_photo-1673697239981-389164b7b87f?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OXx8bmF0dXJlfGVufDB8fDB8fHww"
                          }
                          alt=""
                          className="w-full h-full object-cover rounded"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white/90 line-clamp-2 mb-1">
                          {item.prompt}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-white/60">
                          <span>{item.tags[0]}</span>
                          <span>•</span>
                          <span>{item.type}</span>
                        </div>
                        <p className="text-xs text-white/40 mt-1">
                          {new Date(
                            (item.createdAt as Timestamp).seconds * 1000
                          ).toLocaleDateString()}
                        </p>
                        {currentHistoryId === item.id && (
                          <div className="flex items-center gap-1 mt-2">
                            <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                            <span className="text-xs text-purple-400">
                              Active
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {history.length === 0 && (
                <div className="text-center py-12">
                  <History size={48} className="text-white/20 mx-auto mb-4" />
                  <p className="text-white/60">No edits yet</p>
                  <p className="text-white/40 text-sm mt-1">
                    Your editing history will appear here
                  </p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {publishModalOpen && selectedImageForPublish && (
        <PublishContent
          item={selectedImageForPublish}
          onClose={() => {
            setPublishModalOpen(false);
            setSelectedImageForPublish(null);
          }}
        />
      )}

      <UploadsPanel
        isOpen={isUploadsPanelOpen}
        onClose={() => setIsUploadsPanelOpen(false)}
        onImageSelect={handleImageSelect}
      />

      {overlayImage && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center"
          onClick={() => setOverlayImage(null)}
        >
          <motion.img
            src={overlayImage}
            alt="Full size"
            className="max-w-[90vw] max-h-[90vh] object-contain"
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.8 }}
          />
        </motion.div>
      )}
      <InsufficientFluxModal
        isOpen={isFluxModalOpen}
        onClose={() => setIsFluxModalOpen(false)}
      />
    </div>
  );
};

// Empty State Component
const EmptyState: React.FC = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    className="flex items-center justify-center h-full"
  >
    <div className="text-center max-w-md">
      <div className="w-24 h-24 mx-auto mb-6 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-blue-500/20 rounded-full animate-pulse"></div>
        <div className="absolute inset-2 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center">
          <Sparkles size={32} className="text-white" />
        </div>
      </div>
      <h2 className="text-2xl font-bold text-white mb-3">Image Editor</h2>
      <p className="text-white/60 mb-6 leading-relaxed">
        Upload an image, describe your desired changes, and let the AI work its
        magic.
      </p>
    </div>
  </motion.div>
);

interface ResultsGridProps {
  image: LibraryItem;
  originalImage: string | null;
  onPublish: (image: LibraryItem) => void;
  onImageClick: (url: string) => void;
  onContinueEditing: (image: LibraryItem) => void;
}

const ResultsGrid: React.FC<ResultsGridProps> = ({
  image,
  originalImage,
  onPublish,
  onImageClick,
  onContinueEditing,
}) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.95 },
    visible: { opacity: 1, y: 0, scale: 1 },
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="h-full"
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-white/90 flex items-center gap-2">
          <Sparkles size={20} className="text-purple-400" />
          Your Edited Image
        </h2>
      </div>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-4"
      >
        {image.originalImageUrl && (
          <motion.div variants={itemVariants}>
            <h3 className="text-center text-white/80 mb-2">Original</h3>
            <CompactImageCard
              image={
                {
                  id: "original",
                  fullUrl: image.originalImageUrl,
                } as LibraryItem
              }
              onPublish={() => {}}
              onClick={() => onImageClick(image.originalImageUrl!)}
              onContinueEditing={() => {}}
            />
          </motion.div>
        )}
        <motion.div variants={itemVariants}>
          <h3 className="text-center text-white/80 mb-2">Edited</h3>
          <CompactImageCard
            image={image}
            onPublish={onPublish}
            onClick={() => onImageClick(image.fullUrl)}
            onContinueEditing={onContinueEditing}
          />
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

interface ImageCardProps {
  image: LibraryItem;
  onPublish: (image: LibraryItem) => void;
  onClick: () => void;
  onContinueEditing: (image: LibraryItem) => void;
}

const CompactImageCard: React.FC<ImageCardProps> = ({
  image,
  onPublish,
  onClick,
  onContinueEditing,
}) => {
  const [isLiked, setIsLiked] = useState(false);

  const handleLike = () => setIsLiked(!isLiked);
  const handleDownload = () => {
    if (image.fullUrl) {
      const link = document.createElement("a");
      link.href = image.fullUrl;
      link.download = "edited_image.png";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };
  const handleRegenerate = () => console.log("Regenerate:", image.id);

  return (
    <motion.div
      className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden group"
      whileHover={{ y: -4 }}
      transition={{ duration: 0.2 }}
    >
      <div className="relative" onClick={onClick}>
        <img
          src={
            image.fullUrl ||
            "https://plus.unsplash.com/premium_photo-1673697239981-389164b7b87f?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OXx8bmF0dXJlfGVufDB8fDB8fHww"
          }
          alt={image.prompt}
          className="w-full aspect-square object-cover"
        />
      </div>

      {image.id !== "original" && (
        <div className="p-3">
          <div className="flex items-center justify-center gap-2">
            <motion.button
              onClick={() => onContinueEditing(image)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="flex-1 p-3 rounded-lg backdrop-blur-sm bg-white/10 hover:bg-white/20 text-white/80 border border-white/20 transition-all text-sm font-medium"
            >
              <Wand2 size={16} className="mx-auto" />
            </motion.button>

            <motion.button
              onClick={handleDownload}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="flex-1 p-3 rounded-lg backdrop-blur-sm bg-white/10 hover:bg-white/20 text-white/80 border border-white/20 transition-all text-sm font-medium"
            >
              <Download size={16} className="mx-auto" />
            </motion.button>

            <motion.button
              onClick={() => onPublish(image)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="flex-1 p-3 rounded-lg backdrop-blur-sm bg-purple-600/40 hover:bg-purple-600/60 text-purple-300 border border-purple-500/50 transition-all text-sm font-medium"
            >
              <Share size={16} className="mx-auto" />
            </motion.button>

            <motion.button
              onClick={handleRegenerate}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="flex-1 p-3 rounded-lg backdrop-blur-sm bg-white/10 hover:bg-white/20 text-white/80 border border-white/20 transition-all text-sm font-medium"
            >
              <RefreshCw size={16} className="mx-auto" />
            </motion.button>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default ImageEditor;
